import { useEffect, useState } from 'preact/hooks';
import { buyEngine, iapAvailable, iapOnSale, iapPrice, iapPriceNow, iapStore, onIapReady, NOT_ON_SALE } from '../utils/iap';
import { currentSession } from '../core/Session';
import { t } from '../i18n';

/**
 * Buying an engine from the phone app: one button, Apple's sheet, and the engine unlocked when the server
 * has verified the transaction. Everywhere else `canBuy` is false and the caller keeps the web checkout.
 *
 * Restoring is NOT here on purpose: it must be reachable without a car in mind (Apple), so it lives in
 * <RestoreButton /> — the menu drawer and the unlock page both show that one.
 */
export function useBuy(carId: string, onDone?: () => void) {
  const [canBuy, setCanBuy] = useState(iapAvailable());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [price, setPrice] = useState(iapPriceNow(carId));   // the store's price for THIS engine, once it has answered
  // NOT ON SALE IS KNOWN BEFORE THE TAP. An engine with no product in this store offered its web price
  // and a PURCHASE button that could only fail ("The purchase did not go through. Nothing has been
  // charged. this engine is not on sale in the app yet"). Now the button says so up front.
  const [onSale, setOnSale] = useState<boolean | null>(null);
  useEffect(() => onIapReady(() => setCanBuy(true)), []);
  useEffect(() => { if (canBuy) void iapPrice(carId).then((p) => { if (p) setPrice(p); }); }, [canBuy, carId]);
  useEffect(() => { if (canBuy) void iapOnSale(carId).then((v) => { if (v !== null) setOnSale(v); }); }, [canBuy, carId]);
  useEffect(() => { if (onSale === false) setNote(t('notInAppYetFine')); }, [onSale]);
  // Android buys through Google Play: "Opening the App Store" was the wrong shop on half the phones
  const busyLabel = t(iapStore() === 'google' ? 'buyingPlay' : 'buying');
  const buy = async () => {
    if (busy || onSale === false) return;
    setBusy(true);
    setNote(busyLabel);
    const r = await buyEngine(carId);
    setBusy(false);
    if (r === 'ok') {
      setNote(t('buyDone'));
      await currentSession()?.refreshGrant().catch(() => null);
      onDone?.();
    } else if (r === 'cancelled') setNote(t('buyCancelled'));
    // an older app learns the store has no such product only when it tries to sell it
    else if (r === NOT_ON_SALE || r.startsWith('product not found')) setOnSale(false);
    else {
      // SAY WHY. Hiding the reason behind "it didn't go through" cost an evening of guessing (Marc's
      // purchase was failing because no product existed in App Store Connect, and the app knew that all
      // along). The reasons are already plain sentences: not on sale yet, the store did not answer, or
      // whatever Apple itself said.
      // eslint-disable-next-line no-console
      console.warn('purchase failed:', r);
      setNote(`${t('buyFailed')} ${r}`);
    }
  };
  return { canBuy, busy, note, price, buy, onSale, busyLabel };
}
